import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, MailPlus, RotateCcw, Save, Eye, Check, SendHorizontal, Bell } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";

const CHANNEL_META = {
  email: { icon: Mail, label: "Email", color: "#17A88A" },
  sms: { icon: MessageSquare, label: "SMS", color: "#084438" },
  both: { icon: MailPlus, label: "Email or SMS", color: "#0B5E4E" },
  portal: { icon: Bell, label: "Portal message", color: "#0B5E4E" },
};

const SAMPLE = {
  first_name: "John", last_name: "Doe", login_id: "john.doe@example.com",
  temp_password: "Grace2026", course_name: "Discipleship", lesson_title: "Lesson 1",
  tutor_name: "Pastor Grace", feedback: "Feedback: Great work — keep it up!",
};

function renderPreview(text, ctx) {
  let out = text || "";
  Object.entries(ctx).forEach(([k, v]) => { out = out.split(`{${k}}`).join(v); });
  return out;
}

function TemplateCard({ tpl, placeholders, ctxExtra, onSaved }) {
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [customized, setCustomized] = useState(tpl.customized);
  const [testing, setTesting] = useState(false);
  const bodyRef = useRef();
  const tokens = tpl.placeholders || placeholders;

  const meta = CHANNEL_META[tpl.channel] || CHANNEL_META.email;
  const Icon = meta.icon;
  const dirty = subject !== tpl.subject || body !== tpl.body;
  const ctx = { ...SAMPLE, ...ctxExtra };

  const insertToken = (token) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  const save = async () => {
    if (!body.trim()) { toast.error("Message body can't be empty."); return; }
    setSaving(true);
    try {
      await api.put(`/templates/${tpl.key}`, { subject, body });
      toast.success(`"${tpl.label}" saved`);
      setCustomized(true);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset "${tpl.label}" to the original default wording?`)) return;
    try {
      const { data } = await api.post(`/templates/${tpl.key}/reset`);
      setSubject(data.subject); setBody(data.body); setCustomized(false);
      toast.success(`"${tpl.label}" reset to default`);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendTest = async () => {
    if (!body.trim()) { toast.error("Add some message text first."); return; }
    setTesting(true);
    try {
      const chan = tpl.channel === "both" ? "email" : tpl.channel;
      const { data } = await api.post(`/templates/${tpl.key}/test`, { subject, body, channel: chan });
      if (data.status === "sent") toast.success(`Test ${data.channel === "sms" ? "text" : data.channel === "portal" ? "portal message" : "email"} sent to ${data.target}`);
      else if (data.status === "simulated") toast.success(`Test queued (simulated — ${data.channel} not available for your account)`);
      else toast.error(`Test failed to ${data.target}. Check the address/number is valid.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <div className={`${CARD} p-6`} data-testid={`template-card-${tpl.key}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: meta.color + "1A", color: meta.color }}>
              <Icon className="h-3.5 w-3.5" />{meta.label}
            </span>
            <h3 className="font-head text-lg font-bold" style={{ color: "var(--sj-text)" }}>{tpl.label}</h3>
            {customized && (
              <span data-testid={`template-customized-${tpl.key}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#0B5E4E1A", color: "#0B5E4E" }}>Customised</span>
            )}
          </div>
          <p className="mt-1 text-sm" style={{ color: "var(--sj-text-2)" }}>{tpl.description}</p>
        </div>
      </div>

      {tpl.has_subject && (
        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Subject line</label>
          <input data-testid={`template-subject-${tpl.key}`} value={subject} onChange={(e) => setSubject(e.target.value)} className={`${inputCls} mt-1.5`} />
        </div>
      )}

      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Message body</label>
        <textarea ref={bodyRef} data-testid={`template-body-${tpl.key}`} rows={tpl.channel === "sms" ? 4 : 8} value={body} onChange={(e) => setBody(e.target.value)} className={`${inputCls} mt-1.5 font-mono leading-relaxed`} />
      </div>

      <div className="mt-3">
        <div className="text-xs mb-1.5" style={{ color: "var(--sj-text-3)" }}>Click to insert a placeholder (auto-filled when sent):</div>
        <div className="flex flex-wrap gap-1.5">
          {tokens.map((p) => (
            <button key={p} type="button" data-testid={`token-${tpl.key}-${p.replace(/[{}]/g, "")}`} onClick={() => insertToken(p)}
              className="text-xs font-mono px-2.5 py-1 rounded-lg border transition-colors hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {showPreview && (
        <div className="mt-4 rounded-xl border p-4" style={{ borderColor: "var(--sj-border)", background: "#EDF2F4" }} data-testid={`template-preview-${tpl.key}`}>
          {tpl.has_subject && <div className="text-sm font-semibold mb-2" style={{ color: "var(--sj-text)" }}>{renderPreview(subject, ctx)}</div>}
          <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--sj-text-2)" }}>{renderPreview(body, ctx)}</div>
        </div>
      )}

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <button data-testid={`template-save-${tpl.key}`} onClick={save} disabled={saving || !dirty}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40 transition-opacity" style={{ background: "var(--sj-primary)" }}>
          {saving ? "Saving…" : <><Save className="h-4 w-4" />Save changes</>}
        </button>
        <button data-testid={`template-preview-toggle-${tpl.key}`} onClick={() => setShowPreview((s) => !s)}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium border transition-colors hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
          {showPreview ? <><Check className="h-4 w-4" />Hide preview</> : <><Eye className="h-4 w-4" />Preview</>}
        </button>
        <button data-testid={`template-test-${tpl.key}`} onClick={sendTest} disabled={testing}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium border disabled:opacity-40 transition-colors hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
          <SendHorizontal className="h-4 w-4" />{testing ? "Sending…" : "Send test to me"}
        </button>
        <button data-testid={`template-reset-${tpl.key}`} onClick={reset} disabled={!customized}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium border disabled:opacity-40 transition-colors hover:bg-[#E3F1EC] ml-auto" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
          <RotateCcw className="h-4 w-4" />Reset to default
        </button>
      </div>
    </div>
  );
}

export default function MessageTemplates() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setData(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ctxExtra = { portal_url: data?.portal_url || "", church_name: data?.church_name || "" };

  return (
    <div className="space-y-6" data-testid="message-templates-page">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>Message Templates</h1>
        <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--sj-text-2)" }}>
          Edit the wording of the automated messages your members and leaders receive. Placeholders like <code className="font-mono text-xs px-1 rounded" style={{ background: "#EBE9E4" }}>{"{first_name}"}</code> are filled in automatically when each message is sent.
        </p>
      </div>

      {loading ? (
        <div className={`${CARD} p-10 text-center text-sm`} style={{ color: "var(--sj-text-3)" }}>Loading templates…</div>
      ) : !data ? (
        <div className={`${CARD} p-10 text-center text-sm`} style={{ color: "var(--sj-text-3)" }}>Couldn't load templates.</div>
      ) : (
        <div className="grid grid-cols-1 gap-5">
          {data.templates.map((tpl) => (
            <TemplateCard key={tpl.key} tpl={tpl} placeholders={data.placeholders} ctxExtra={ctxExtra} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
}
